import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE, capitalizeFirst } from './helpers';
import { UserPlus, Edit2, Save, XCircle, Mail, Check, Copy } from 'lucide-react';

const NEW_USER_DEFAULTS = { email: '', firstName: '', lastName: '', role: 'user', hasCRMAccess: true, hasDMAccess: false, hasInvestorAdminAccess: false };

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newUser, setNewUser] = useState(NEW_USER_DEFAULTS);
  const [createdUser, setCreatedUser] = useState(null);
  const [copied, setCopied] = useState(false);
  // Current user's id — used to flag the admin's own row for the self-edit
  // lockout guard below. Fetched separately because UsersManagement is not
  // wired into the AuthContext defined in App.js.
  const [currentUserId, setCurrentUserId] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/users`);
      setUsers(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/users/me`)
      .then(res => setCurrentUserId(res.data.id))
      .catch(() => { /* non-fatal — self-edit guard just won't activate */ });
  }, []);

  const CRM_URL = 'https://crm.cagan.com';

  // Build the invite mailto. Subject + body vary by which app(s) the new user
  // has access to: CRM only (today's behavior), DM only, or both. The button
  // labels referenced in the bodies match the LoginScreen verbatim ("CRM
  // Login" / "Document Manager Login") so the invitee sees what's described.
  const buildMailtoLink = (email, firstName, hasCRMAccess, hasDMAccess) => {
    let subject;
    let body;
    if (hasDMAccess && !hasCRMAccess) {
      subject = "You've been invited to Cagan Document Manager";
      body = `Hi ${firstName},\n\nYou've been added to Cagan Document Manager. To access it, go to:\n${CRM_URL}\n\nClick 'Document Manager Login' and use your Cagan email to log in. No password setup needed — it uses your existing Microsoft 365 account.\n\nIf you have any questions, let me know.`;
    } else if (hasCRMAccess && hasDMAccess) {
      subject = "You've been invited to Cagan CRM and Document Manager";
      body = `Hi ${firstName},\n\nYou've been added to Cagan CRM and Cagan Document Manager. To access them, go to:\n${CRM_URL}\n\nClick 'CRM Login' or 'Document Manager Login' depending on which app you want — your account has access to both. Use your Cagan email to log in. No password setup needed — it uses your existing Microsoft 365 account.\n\nIf you have any questions, let me know.`;
    } else {
      // Default — CRM only (also covers any legacy caller that doesn't pass flags).
      subject = "You've been invited to Cagan CRM";
      body = `Hi ${firstName},\n\nYou've been added to the Cagan CRM system. To access it, go to:\n${CRM_URL}\n\nClick 'Sign in with Microsoft' and use your Cagan email to log in. No password setup needed — it uses your existing Microsoft 365 account.\n\nIf you have any questions, let me know.`;
    }
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(CRM_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/users`, newUser);
      setCreatedUser({
        email: newUser.email,
        firstName: newUser.firstName,
        hasCRMAccess: newUser.hasCRMAccess,
        hasDMAccess: newUser.hasDMAccess,
      });
      setNewUser(NEW_USER_DEFAULTS);
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.UserId);
    setEditData({
      firstName: user.FirstName,
      lastName: user.LastName,
      role: user.Role,
      isActive: user.IsActive,
      hasCRMAccess: !!user.HasCRMAccess,
      hasDMAccess: !!user.HasDMAccess,
      hasInvestorAdminAccess: !!user.HasInvestorAdminAccess,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (userId) => {
    try {
      await axios.put(`${API_BASE}/users/${userId}`, editData);
      setEditingId(null);
      setEditData({});
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <div className="crm-loading">
        <div className="crm-loading__container">
          <div className="crm-loading__spinner" />
          <p className="crm-loading__text">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crm-section">
      <div className="crm-section__header">
        <h2 className="crm-section__title">Manage Users</h2>
        <button className="crm-btn crm-btn--primary" onClick={() => setShowAddForm(!showAddForm)}>
          <UserPlus size={16} />
          Add User
        </button>
      </div>

      {error && (
        <div className="crm-error" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>
            <XCircle size={16} />
          </button>
        </div>
      )}

      {createdUser && (
        <div className="crm-card" style={{ padding: '1.25rem', marginBottom: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Check size={18} style={{ color: '#16a34a' }} />
            <strong style={{ color: '#15803d' }}>User added successfully</strong>
            <button
              onClick={() => setCreatedUser(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
            >
              <XCircle size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a
              href={buildMailtoLink(createdUser.email, createdUser.firstName, createdUser.hasCRMAccess, createdUser.hasDMAccess)}
              className="crm-btn crm-btn--primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <Mail size={16} />
              Send Invite to {createdUser.email}
            </a>
            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              CRM link: <code style={{ background: '#e5e7eb', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{CRM_URL}</code>
              <button
                onClick={handleCopyUrl}
                style={{ marginLeft: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', verticalAlign: 'middle' }}
                title="Copy URL"
              >
                {copied ? <Check size={14} style={{ color: '#16a34a' }} /> : <Copy size={14} />}
              </button>
            </span>
          </div>
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAdd} className="crm-card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Add New User</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="crm-form-group">
              <label className="crm-form-label">Email *</label>
              <input
                type="email"
                className="crm-form-input"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </div>
            <div className="crm-form-group">
              <label className="crm-form-label">First Name *</label>
              <input
                type="text"
                className="crm-form-input"
                value={newUser.firstName}
                onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                required
              />
            </div>
            <div className="crm-form-group">
              <label className="crm-form-label">Last Name *</label>
              <input
                type="text"
                className="crm-form-input"
                value={newUser.lastName}
                onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                required
              />
            </div>
            <div className="crm-form-group">
              <label className="crm-form-label">Role</label>
              <select
                className="crm-form-input"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="crm-form-group" style={{ marginTop: '1rem' }}>
            <label className="crm-form-label">Access</label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={newUser.hasCRMAccess}
                  onChange={(e) => setNewUser({ ...newUser, hasCRMAccess: e.target.checked })}
                />
                CRM Access
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={newUser.hasDMAccess}
                  onChange={(e) => setNewUser({ ...newUser, hasDMAccess: e.target.checked })}
                />
                DM Access
              </label>
              {/* Investor Admin is a permission inside the CRM (the investor
                  portal's staff endpoints), not an application on its own — it
                  is intentionally not part of the "at least one" rule below. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={newUser.hasInvestorAdminAccess}
                  onChange={(e) => setNewUser({ ...newUser, hasInvestorAdminAccess: e.target.checked })}
                />
                Investor Admin
              </label>
            </div>
            {!newUser.hasCRMAccess && !newUser.hasDMAccess && (
              <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.8rem', color: 'var(--danger)' }}>
                User must have access to at least one application
              </span>
            )}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              type="submit"
              className="crm-btn crm-btn--primary"
              disabled={!newUser.hasCRMAccess && !newUser.hasDMAccess}
            >
              Create User
            </button>
            <button type="button" className="crm-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="crm-card" style={{ overflow: 'auto' }}>
        <table className="crm-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Access</th>
              <th>Active</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.UserId}>
                <td>{user.Email}</td>
                <td>
                  {editingId === user.UserId ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        className="crm-form-input"
                        value={editData.firstName}
                        onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                        style={{ width: '100px' }}
                      />
                      <input
                        type="text"
                        className="crm-form-input"
                        value={editData.lastName}
                        onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                        style={{ width: '100px' }}
                      />
                    </div>
                  ) : (
                    `${user.FirstName} ${user.LastName}`
                  )}
                </td>
                <td>
                  {editingId === user.UserId ? (
                    <select
                      className="crm-form-input"
                      value={editData.role}
                      onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <span className={`crm-badge ${user.Role === 'admin' ? 'crm-badge--purple' : 'crm-badge--taupe'}`}>
                      {capitalizeFirst(user.Role)}
                    </span>
                  )}
                </td>
                <td>
                  {editingId === user.UserId ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {/* Self-edit lockout: if editing own row, disable the
                          last-checked checkbox so the admin can't toggle
                          themselves into (0,0). Backend enforces the same. */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={!!editData.hasCRMAccess}
                          disabled={editingId === currentUserId && editData.hasCRMAccess && !editData.hasDMAccess}
                          onChange={(e) => setEditData({ ...editData, hasCRMAccess: e.target.checked })}
                          title={editingId === currentUserId && editData.hasCRMAccess && !editData.hasDMAccess
                            ? 'You cannot remove your own access to all applications'
                            : ''}
                        />
                        CRM
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={!!editData.hasDMAccess}
                          disabled={editingId === currentUserId && editData.hasDMAccess && !editData.hasCRMAccess}
                          onChange={(e) => setEditData({ ...editData, hasDMAccess: e.target.checked })}
                          title={editingId === currentUserId && editData.hasDMAccess && !editData.hasCRMAccess
                            ? 'You cannot remove your own access to all applications'
                            : ''}
                        />
                        DM
                      </label>
                      {/* No self-lockout guard: this flag is not application
                          access, and admins keep investor-admin reach via the
                          role branch of requireInvestorAdmin. */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={!!editData.hasInvestorAdminAccess}
                          onChange={(e) => setEditData({ ...editData, hasInvestorAdminAccess: e.target.checked })}
                        />
                        Investor Admin
                      </label>
                      {!editData.hasCRMAccess && !editData.hasDMAccess && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--danger)' }}>
                          Pick at least one
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {user.HasCRMAccess && <span className="crm-badge crm-badge--blue">CRM</span>}
                      {user.HasDMAccess && <span className="crm-badge crm-badge--yellow">DM</span>}
                      {user.HasInvestorAdminAccess && <span className="crm-badge crm-badge--taupe">Investor Admin</span>}
                    </div>
                  )}
                </td>
                <td>
                  {editingId === user.UserId ? (
                    <select
                      className="crm-form-input"
                      value={editData.isActive ? '1' : '0'}
                      onChange={(e) => setEditData({ ...editData, isActive: e.target.value === '1' })}
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  ) : (
                    <span className={`crm-badge ${user.IsActive ? 'crm-badge--green' : 'crm-badge--red'}`}>
                      {user.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td>{user.LastLoginDate ? new Date(user.LastLoginDate).toLocaleDateString() : 'Never'}</td>
                <td>
                  {editingId === user.UserId ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="crm-btn crm-btn--small crm-btn--primary"
                        onClick={() => saveEdit(user.UserId)}
                        disabled={!editData.hasCRMAccess && !editData.hasDMAccess}
                      >
                        <Save size={14} /> Save
                      </button>
                      <button className="crm-btn crm-btn--small" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button className="crm-btn crm-btn--small" onClick={() => startEdit(user)}>
                      <Edit2 size={14} /> Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
